import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { withAlphamartIntlProvider } from 'components/shared/AlphamartIntlProvider';
import { LoadableContent } from 'components/shared/Loader';
import { AccessFlag, ErrorCode, MarginVisibility, PERMISSIONS, UserRoles } from 'shared/constants';
import { useAuthorization } from 'shared/helpers';
import { useCurrentUser } from 'shared/hooks';
import { useAlphamartNavigate } from 'shared/hooks/useAlphamartRouter';
import { useGetCompanies } from 'shared/queries';
import { AlphamartErrorData, BooleanString, ManageUserAccesses, UserFormData } from 'shared/types';
import { fetchUser as fetchCurrentUser, setTwoFactorAuthentication } from 'store/auth';
import { useAppDispatch, useAppSelector } from 'store/shared/hooks';
import { hideModal, showModal } from 'store/shared/modal';
import { snackBarPushFailure, snackBarPushSuccess } from 'store/shared/snackBarSlice';
import { updateUser } from 'store/updateUserSlice';
import { fetchUser } from 'store/userDetailsSlice';
import { useProcessMarginWarnings } from '../hooks/useProcessMarginWarnings';
import { messages, useTypedIntl } from '../locale/messages';
import { UserForm } from '../UserForm/UserForm';

function UpdateUserComponent(): React.ReactElement {
  const intl = useTypedIntl();
  const navigate = useAlphamartNavigate();
  const dispatch = useAppDispatch();
  const authorize = useAuthorization();
  const processWarnings = useProcessMarginWarnings();
  const userId = Number(useParams<{ id: string }>().id);
  const { user, isPending } = useAppSelector(({ userDetails }) => userDetails);
  const { isPending: isUserUpdatePending } = useAppSelector(state => state.updateUser);
  const currentUser = useCurrentUser();
  const allowedToFetchCompanies = authorize(PERMISSIONS.COMPANIES.LIST);
  const { data: fetchedCompanies } = useGetCompanies({}, { enabled: allowedToFetchCompanies });
  const companies = allowedToFetchCompanies ? fetchedCompanies?.data : [currentUser.company];

  useEffect(() => {
    dispatch(fetchUser(userId));
  }, [dispatch]);

  const allowedToChangeTermsAdjustment = authorize(PERMISSIONS.USERS.UPDATE_TERMS_ADJUSTMENTS);
  const allowedToChangeDisplayHedgePrices = authorize(
    PERMISSIONS.USERS.SHOW_DISPLAY_HEDGE_PRICES_FIELD,
  );
  const initialValues: UserFormData | undefined = user && {
    info: {
      company: user.company.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      mobilePhone: user.mobilePhone,
      mobilePhonePrefix: user.mobilePhonePrefix,
      officePhone: user.officePhone || '',
      officePhonePrefix: user.officePhonePrefix || '',
    },
    roles: {
      roles: user.roles?.map(r => r.id) || [],
      chosenRoles: [],
      userAccessFlags: {
        ...Object.fromEntries(Object.values(AccessFlag).map(flag => [flag, false])),
        ...Object.fromEntries(user?.userAccessFlags?.map(key => [key, true])),
      },
      privileges: {
        additionalDevice: (user.privileges.additionalDevice ?? false).toString() as BooleanString,
      },
    },
    prices: {
      marginVisibility: user.marginVisibility,
      userProfitMargin: user.profitMargins || [{ value: user.selectedProfitMargin }],
      assignedHedges: user.assignedHedges?.map(({ id }) => id) ?? [],
      prices: {
        ...user.prices,
        ptHedgeUsed: user.prices?.ptHedgeUsed?.id,
        pdHedgeUsed: user.prices?.pdHedgeUsed?.id,
        rhHedgeUsed: user.prices?.rhHedgeUsed?.id,
      },
      ...(user.roles.some(({ name }) => name === UserRoles.BUYER)
        ? {
            ptTermsAdjustment: user.ptTermsAdjustment,
            pdTermsAdjustment: user.pdTermsAdjustment,
            rhTermsAdjustment: user.rhTermsAdjustment,
          }
        : {}),
      currentProfitMarginPt: user.currentProfitMarginPt,
      currentProfitMarginPd: user.currentProfitMarginPd,
      currentProfitMarginRh: user.currentProfitMarginRh,
    },
  };

  const handleSuccess = () => {
    dispatch(
      snackBarPushSuccess(
        intl.formatMessage({
          id: 'UserForm.Message.SuccessUpdate',
        }),
      ),
    );

    navigate(`/users/${userId}`);
  };

  const handlePhoneDuplication = async (
    values: UserFormData,
    parseUserParams: ManageUserAccesses,
    error: AlphamartErrorData,
  ): Promise<unknown> =>
    dispatch(
      showModal({
        message: intl.formatMessage(
          { id: 'UserForm.Modal.Update.ConfirmDuplicateMobilePhone' },
          error.errorData,
        ),
        onClose: () => {
          dispatch(hideModal());
        },
        onConfirm: async () => {
          await dispatch(updateUser(values, userId, parseUserParams, true));
          handleSuccess();
          dispatch(hideModal());
        },
      }),
    );

  const handleEmailOrPhoneError = async (error: AlphamartErrorData): Promise<void> => {
    const isPrivileged = authorize(PERMISSIONS.USERS.KNOW_ABOUT_OTHER_COMPANY_USERS);
    const errorCode = error?.errorCode as
      | ErrorCode.EMAIL_ALREADY_USED
      | ErrorCode.MOBILE_PHONE_ALREADY_USED;
    dispatch(
      snackBarPushFailure(
        intl.formatMessage(
          { id: `UserForm.Errors.${errorCode}${isPrivileged ? '' : '.ADMIN'}` },
          error?.errorData,
        ),
      ),
    );
  };

  const onSubmit = async (values: UserFormData) => {
    if (!companies) return;
    const selectedCompany = [...companies].find(c => c.id === values?.info.company);
    const canUseMarginSliderPerMetal = selectedCompany?.canUseProfitMarginPerMetal;
    const parseUserParams = {
      allowedToChangeTermsAdjustment,
      allowedToChangeDisplayHedgePrices,
      canUseMarginSlider:
        values.prices.marginVisibility === MarginVisibility.SLIDER_AND_TOTAL_MARGIN ||
        values.prices.marginVisibility === MarginVisibility.SLIDER_MARGIN_ONLY,
      canUseMarginSliderPerMetal,
    };

    try {
      await dispatch(updateUser(values, userId, parseUserParams));
      handleSuccess();
    } catch (e) {
      const error = e as AlphamartErrorData;
      const isMobilePhoneDuplicated =
        authorize(PERMISSIONS.USERS.CREATE_UPDATE_WITH_DUPLICATED_MOBILE_PHONE) &&
        error?.errorCode === ErrorCode.MOBILE_PHONE_ALREADY_USED_RETRY_ALLOWED;
      const isMobilePhoneOrEmailDuplicated = [
        ErrorCode.EMAIL_ALREADY_USED,
        ErrorCode.MOBILE_PHONE_ALREADY_USED,
        ErrorCode.EMAIL_ALREADY_USED_IN_OTHER_COMPANY,
      ].includes(error?.errorCode);
      const twoFactorAuthenticationError =
        error?.errorCode === ErrorCode.TWO_FACTOR_AUTHENTICATION_VERIFICATION_FAILED;

      if (isMobilePhoneDuplicated) {
        await handlePhoneDuplication(values, parseUserParams, error);
      } else if (isMobilePhoneOrEmailDuplicated) {
        await handleEmailOrPhoneError(error);
      } else if (twoFactorAuthenticationError) {
        dispatch(
          setTwoFactorAuthentication({
            isRequired: true,
            userAccessFlags: values.roles.userAccessFlags,
            callback: () => onSubmit(values),
          }),
        );
      } else {
        dispatch(
          snackBarPushFailure(intl.formatMessage({ id: 'Global.Error.SomethingWentWrong' })),
        );
      }
    }

    if (currentUser.id === +userId) dispatch(fetchCurrentUser());
  };

  return (
    <LoadableContent loading={isPending || isUserUpdatePending}>
      {user && (
        <UserForm
          onSubmit={processWarnings(onSubmit)}
          initialValues={initialValues}
          editMode
          allowRolesChange={user?.id !== currentUser?.id}
        />
      )}
    </LoadableContent>
  );
}

export const UpdateUser = withAlphamartIntlProvider(UpdateUserComponent, messages);
